/**
 * The inbox — one destination, two tabs (2026-09-06).
 *
 * There used to be three: the header bell (last ten, mark read), this page
 * (a read-only table you could sort but not act on), and a separate
 * "Inbox" page reachable only when a rail badge happened to appear. A person
 * could not tell which one to look at, and the owner's report was blunt: the
 * app "is not letting me clear things easily". It was not — nothing on this
 * page could mark, dismiss or clear anything, and the motivator's delete
 * endpoint had never been wired to a proxy, so the capability existed and was
 * unreachable.
 *
 * What a notification centre is expected to do, and now does:
 *   - say what is unread, and let you see only that
 *   - open the thing a notification is about, marking it read on the way
 *   - dismiss one (final: the store has no undelete, so no Undo is offered)
 *   - mark everything read, and clear what you have read
 *   - keep the arrival time, in the reader's own zone
 *
 * The second tab is the decision queue: things the assistant heard that are
 * not commitments yet. That is a different question ("what needs an answer
 * from me") from a notification ("what were I told"), so it stays a separate
 * tab rather than being mixed into one list — but it is no longer a separate
 * page you had to already know about.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Bell, CheckCheck, Inbox, RefreshCw, Trash2, X, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { EmptyState } from '../components/ui/EmptyState'
import { useToast } from '../components/ui/Toast'
import { useUserTimezone } from '../hooks/useUserTimezone'
import { HortonInbox } from './HortonInbox'
import { BRAND } from '../config/brand'

export interface NotificationHistoryRow {
  id?: string | number
  title?: string | null
  message?: string | null
  severity?: string | null
  priority_score?: number | null
  created_at?: string | null
  read_at?: string | null
  expires_at?: string | null
  thread_key?: string | null
  /** What the notification is about, when the sender named it. */
  item_type?: string | null
  item_id?: string | number | null
}

interface HistoryResponse {
  rows: NotificationHistoryRow[]
  total: number
  page: number
  per_page: number
}

const HISTORY_ENDPOINT = '/api/orchestrator/notifications/history'
const PER_PAGE = 25

/** Server timestamps are UTC; a naive string would be read as local time. */
function parseServerUtc(value?: string | null): Date | null {
  if (!value) return null
  const trimmed = value.trim()
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)
  const d = new Date(hasZone ? trimmed : `${trimmed.replace(' ', 'T')}Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Where a notification points, when it names something. */
function targetOf(n: NotificationHistoryRow): string | null {
  if (!n.item_type || n.item_id == null) return null
  const t = String(n.item_type).toLowerCase()
  const routes: Record<string, string> = {
    task: '/tasks', goal: '/goals', habit: '/habits', skill: '/skills', dream: '/dreams',
  }
  return routes[t] ? `${routes[t]}/${n.item_id}` : null
}

function NotificationList() {
  const { formatDateTime, timezone } = useUserTimezone()
  const navigate = useNavigate()
  const toast = useToast()
  const [rows, setRows] = useState<NotificationHistoryRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE), sort: 'received', order: 'desc' })
      const res = await fetch(`${HISTORY_ENDPOINT}?${params}`, { credentials: 'include' })
      if (!res.ok) throw new Error(`Server responded ${res.status}`)
      const data: Partial<HistoryResponse> = await res.json()
      setRows(Array.isArray(data.rows) ? data.rows : [])
      setTotal(typeof data.total === 'number' ? data.total : 0)
    } catch (err) {
      setRows([]); setTotal(0)
      setError(err instanceof Error ? err.message : 'Request failed')
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => { void load() }, [load])

  const shown = useMemo(() => (unreadOnly ? rows.filter((n) => !n.read_at) : rows), [rows, unreadOnly])
  const unreadCount = useMemo(() => rows.filter((n) => !n.read_at).length, [rows])
  const readCount = rows.length - unreadCount
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

  const markRead = async (id: string | number) => {
    setRows((r) => r.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)))
    try {
      await fetch(`/api/orchestrator/notifications/${id}/read`, { method: 'POST', credentials: 'include' })
    } catch { /* the row already reads as read; the next load corrects it */ }
  }

  const open = async (n: NotificationHistoryRow) => {
    const to = targetOf(n)
    if (n.id != null && !n.read_at) await markRead(n.id)
    if (to) navigate(to)
  }

  const dismiss = async (n: NotificationHistoryRow) => {
    if (n.id == null) return
    const before = rows
    setRows((r) => r.filter((x) => x.id !== n.id))
    setTotal((t) => Math.max(0, t - 1))
    try {
      const res = await fetch(`/api/orchestrator/notifications/${n.id}`, { method: 'DELETE', credentials: 'include' })
      if (!res.ok) throw new Error(String(res.status))
      // No Undo here on purpose: the delete has already happened and the
      // motivator has no undelete, so an Undo button would be a lie. The
      // row simply goes, and the toast says so.
      toast.success('Dismissed', n.title || n.message || undefined)
    } catch {
      setRows(before); setTotal((t) => t + 1)
      toast.error('Could not dismiss that', 'Please try again.')
    }
  }

  const markAllRead = async () => {
    setBusy(true)
    const now = new Date().toISOString()
    setRows((r) => r.map((n) => (n.read_at ? n : { ...n, read_at: now })))
    try {
      await fetch('/api/orchestrator/notifications/read-all', { method: 'POST', credentials: 'include' })
    } catch {
      toast.error('Could not mark them read', 'Please try again.')
    } finally { setBusy(false); void load() }
  }

  const clearRead = async () => {
    setBusy(true)
    try {
      const res = await fetch('/api/orchestrator/notifications/clear-read', { method: 'POST', credentials: 'include' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(String(res.status))
      toast.success('Cleared', `${body.deleted ?? 0} read notification${body.deleted === 1 ? '' : 's'} removed.`)
      setPage(1)
    } catch {
      toast.error('Could not clear them', 'Please try again.')
    } finally { setBusy(false); void load() }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div role="radiogroup" aria-label="Show" className="inline-flex rounded-md border border-soft bg-surface p-0.5 text-app-micro">
          {([['all', 'All'], ['unread', 'Unread']] as const).map(([k, label]) => {
            const on = (k === 'unread') === unreadOnly
            return (
              <button key={k} type="button" role="radio" aria-checked={on} onClick={() => setUnreadOnly(k === 'unread')}
                      className={`rounded px-2.5 py-1 font-medium transition-colors ${on ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`}
                      data-testid={`notif-filter-${k}`}>
                {label}{k === 'unread' && unreadCount > 0 ? ` · ${unreadCount}` : ''}
              </button>
            )
          })}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="xs" onClick={() => void markAllRead()} disabled={busy} data-testid="notif-mark-all">
              <CheckCheck className="mr-1.5 h-4 w-4" aria-hidden />Mark all read
            </Button>
          )}
          {readCount > 0 && (
            <Button variant="outline" size="xs" onClick={() => void clearRead()} disabled={busy} data-testid="notif-clear-read">
              <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />Clear read
            </Button>
          )}
          <Button variant="outline" size="xs" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-bad/30 bg-bad/5 p-4" role="alert">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 text-bad" aria-hidden />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-primary">We could not load your notifications just now</p>
              <p className="mt-0.5 text-xs text-secondary">This list is not showing your history — it is showing nothing at all. ({error})</p>
              <Button variant="outline" onClick={() => void load()} className="mt-3">Try again</Button>
            </div>
          </div>
        </div>
      )}

      {!error && loading && <div className="rounded-lg border border-soft p-8 text-center text-sm text-secondary">Loading…</div>}

      {!error && !loading && shown.length === 0 && (
        <div className="rounded-lg border border-soft">
          <EmptyState
            icon={<Bell className="h-7 w-7 text-brand" aria-hidden />}
            title={unreadOnly ? 'Nothing unread' : 'Nothing here yet'}
            description={unreadOnly
              ? 'You are caught up.'
              : `When ${BRAND.assistant} has something to tell you, it shows up here with the time it arrived.`}
          />
        </div>
      )}

      {!error && !loading && shown.length > 0 && (
        <>
          <ul className="divide-y divide-soft rounded-lg border border-soft" data-testid="notif-list">
            {shown.map((n, i) => {
              const unread = !n.read_at
              const to = targetOf(n)
              const at = parseServerUtc(n.created_at)
              return (
                <li key={n.id ?? `${n.thread_key ?? 'row'}-${i}`}
                    className={`flex items-start gap-3 px-4 py-3 ${unread ? 'bg-brand-subtle/40' : ''}`}
                    data-testid="notif-row" data-unread={unread || undefined}>
                  <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${unread ? 'bg-brand' : 'bg-transparent'}`} aria-hidden />
                  <button type="button" onClick={() => void open(n)}
                          className="min-w-0 flex-1 text-left"
                          aria-label={to ? `Open ${n.title || n.message || 'notification'}` : `Mark read: ${n.title || n.message || 'notification'}`}>
                    <p className={`text-sm leading-snug ${unread ? 'font-semibold text-primary' : 'text-secondary'}`}>
                      {n.title || n.message || 'Notification'}
                    </p>
                    {n.message && n.message !== n.title && (
                      <p className="mt-0.5 text-app-meta leading-snug text-secondary">{n.message}</p>
                    )}
                    <p className="mt-1 text-app-micro text-muted">
                      {at ? formatDateTime(at) : '—'}{to ? ' · opens it' : ''}
                    </p>
                  </button>
                  <button type="button" onClick={() => void dismiss(n)} aria-label={`Dismiss ${n.title || n.message || 'notification'}`}
                          title="Dismiss"
                          className="min-h-touch min-w-touch shrink-0 rounded-md text-muted hover:bg-surface-alt hover:text-secondary"
                          data-testid="notif-dismiss">
                    <X className="mx-auto h-4 w-4" aria-hidden />
                  </button>
                </li>
              )
            })}
          </ul>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-app-micro text-muted">Page {page} of {totalPages} · {total} in all · times in {timezone}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="xs" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                  <ChevronLeft className="mr-1 h-4 w-4" aria-hidden />Previous
                </Button>
                <Button variant="outline" size="xs" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  Next<ChevronRight className="ml-1 h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export function Notifications() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'decide' ? 'decide' : 'told'

  return (
    <div className="space-y-4">
      <div className="min-w-0">
        <h1 className="text-lg font-bold text-primary">Inbox</h1>
        <p className="text-xs text-muted">What you were told, and what still needs an answer from you.</p>
      </div>

      <div role="tablist" aria-label="Inbox" className="inline-flex rounded-md border border-soft bg-surface p-0.5 text-app-meta">
        {([['told', 'Notifications', Bell], ['decide', 'To decide', Inbox]] as const).map(([k, label, Icon]) => {
          const on = tab === k
          return (
            <button key={k} type="button" role="tab" aria-selected={on}
                    onClick={() => { const next = new URLSearchParams(params); if (k === 'told') next.delete('tab'); else next.set('tab', k); setParams(next, { replace: true }) }}
                    className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 font-medium transition-colors ${on ? 'bg-brand-subtle text-brand' : 'text-secondary hover:text-primary'}`}
                    data-testid={`inbox-tab-${k}`}>
              <Icon className="h-3.5 w-3.5" aria-hidden />{label}
            </button>
          )
        })}
      </div>

      {tab === 'told' ? <NotificationList /> : <HortonInbox embedded />}
    </div>
  )
}

export default Notifications
